// recova GmbH – Backoffice: Aufträge, Dispo-Planer, Kunden, Telefon-Intake, E-Mail-Queue, Rückfragen, Reservierungen, Rechnungen.
import { useEffect, useState, useCallback } from "react";
import { API, STATUS_LABELS, STATUS_COLORS, fmtErr, eur, fileUrl } from "@/lib/api";
import { DashboardLayout, BACKOFFICE_SECTIONS } from "@/components/DashboardLayout";
import { RouteMap } from "@/components/RouteMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardList, PhoneCall, Mail, Map, MapPin, X, CalendarDays, Users, HelpCircle, Route, ChevronLeft, ChevronRight, Download, Calculator, FileSpreadsheet, FileArchive, XCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_KEYS = Object.keys(STATUS_LABELS);

const PartSearch = ({ onReserve }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) return setResults(null);
    setLoading(true);
    try {
      const r = await API.get("/office/part-search", { params: { q: val } });
      setResults(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div data-testid="part-search">
      <Input placeholder="Ersatzteil suchen (Name oder SKU, z. B. Pumpe / P-1001)…" value={q} onChange={(e) => search(e.target.value)} data-testid="part-search-input" />
      {loading && <p className="mt-1 text-xs text-slate-400">Suche läuft…</p>}
      {results && (
        <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto" data-testid="part-search-results">
          {results.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-2.5 text-xs" data-testid={`part-result-${p.sku}`}>
              <div>
                <span className="font-mono2 font-bold">{p.sku}</span> <span className="font-semibold text-[#0f172a]">{p.name}</span>
                <p className="mt-0.5 text-slate-500">
                  Halle: {p.halle_total > 0
                    ? <span className="font-semibold text-[#1b4332]">{p.halle_total}x – {p.bins.map((b) => b.bin).join(", ")}</span>
                    : <span className="font-semibold text-red-500">nicht vorrätig</span>}
                  {" · "}Fahrzeuge: {p.van_total}x · VK {eur(p.vk)}
                </p>
              </div>
              {onReserve && (
                <Button size="sm" variant="outline" disabled={p.halle_total < 1} onClick={() => onReserve(p)} data-testid={`reserve-part-${p.sku}`}>
                  Reservieren
                </Button>
              )}
            </div>
          ))}
          {results.length === 0 && <p className="text-xs text-slate-400">Kein Teil gefunden – ggf. beim Lieferanten bestellen.</p>}
        </div>
      )}
    </div>
  );
};

const OrderDetail = ({ order, technicians, onClose, onSaved }) => {
  const [form, setForm] = useState({
    technician_id: order.assigned_to || "",
    scheduled_date: order.scheduled_date || "",
    time_window: order.time_window || "",
    status: order.status,
  });
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  const [reservations, setReservations] = useState(order.reservations || []);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const CANCELLABLE = !["abgeschlossen", "totalschaden", "storniert"].includes(order.status);

  useEffect(() => {
    API.get(`/office/orders/${order.id}`)
      .then((r) => setReservations(r.data.reservations || []))
      .catch(() => {});
  }, [order.id]);

  const reserve = async (part) => {
    try {
      const r = await API.post(`/office/orders/${order.id}/reserve`, { part_id: part.id, quantity: 1 });
      setReservations(r.data.reservations);
      toast.success(`${part.sku} für Auftrag reserviert (Pickliste morgen früh)`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const loadSuggestions = async () => {
    if (!form.scheduled_date) return toast.error("Bitte zuerst ein Datum wählen");
    setLoadingSugg(true);
    try {
      const r = await API.get(`/office/suggestions/${order.id}`, { params: { date: form.scheduled_date } });
      setSuggestions(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoadingSugg(false);
    }
  };

  const propose = async () => {
    if (!form.technician_id || !form.scheduled_date || !form.time_window)
      return toast.error("Techniker, Datum und Zeitfenster wählen");
    setBusy(true);
    try {
      await API.post(`/office/orders/${order.id}/propose`, {
        technician_id: form.technician_id,
        date: form.scheduled_date,
        window: form.time_window,
      });
      toast.success("Terminvorschlag an Kunden gesendet");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await API.patch(`/office/orders/${order.id}`, {
        technician_id: form.technician_id || null,
        scheduled_date: form.scheduled_date || null,
        time_window: form.time_window || null,
        status: form.status !== order.status ? form.status : null,
      });
      toast.success("Auftrag aktualisiert");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelOrder = async () => {
    setCancelBusy(true);
    try {
      await API.patch(`/office/orders/${order.id}`, { status: "storniert", note: cancelReason || "Kundenwunsch (telefonisch)" });
      toast.success(`Auftrag ${order.order_number} storniert`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono2" data-testid="order-detail-number">{order.order_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="font-semibold text-[#0f172a]">{order.customer?.name}</p>
            <p className="text-slate-500">{order.customer?.street}, {order.customer?.zip} {order.customer?.city}</p>
            <p className="text-slate-500">{order.customer?.phone} · {order.customer?.email}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="font-semibold">{order.appliance?.brand} {order.appliance?.type_label} {order.appliance?.model}</p>
            {order.appliance?.e_number && <p className="font-mono2 text-xs text-slate-500">E-Nr.: {order.appliance.e_number}</p>}
            {order.appliance?.serial_number && <p className="font-mono2 text-xs text-slate-500">Seriennr.: {order.appliance.serial_number}</p>}
            <p className="mt-1 text-slate-600">{[order.fault?.error_code, ...(order.fault?.issues || []), order.fault?.description].filter(Boolean).join(" · ")}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block truncate">Techniker</Label>
              <select className="w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" value={form.technician_id}
                onChange={(e) => setForm({ ...form, technician_id: e.target.value })} data-testid="assign-technician-select">
                <option value="">Nicht zugewiesen</option>
                {technicians.map((t) => <option key={t.id} value={t.id}>{`${t.name} (${t.van_id || "ohne Van"})`}</option>)}
              </select>
            </div>
            <div>
              <Label className="mb-1 block truncate">Status</Label>
              <select className="w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="order-status-select">
                {STATUS_KEYS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </div>
            <div>
              <Label className="mb-1 block truncate">Termin (Datum)</Label>
              <Input type="date" className="w-full" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid="order-date-input" />
            </div>
            <div>
              <Label className="mb-1 block truncate">Zeitfenster</Label>
              <select className="w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" value={form.time_window}
                onChange={(e) => setForm({ ...form, time_window: e.target.value })} data-testid="order-window-select">
                <option value="">–</option>
                {["vormittags (8-12 Uhr)", "nachmittags (12-17 Uhr)", "abends (17-20 Uhr)"].map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          </div>
          <div className="rounded-xl border border-[#028090]/30 bg-cyan-50/60 p-3" data-testid="dispatch-section">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#028090]">
              <Route className="h-3.5 w-3.5" /> Disposition & Routenvorschlag
            </p>
            <Button variant="outline" size="sm" onClick={loadSuggestions} disabled={loadingSugg || !form.scheduled_date} data-testid="load-suggestions-button">
              {loadingSugg ? "Prüfe…" : "Kapazität & Route prüfen"}
            </Button>
            {suggestions && (
              <div className="mt-2 space-y-1.5" data-testid="suggestions-list">
                {suggestions.suggestions.map((s) => (
                  <button key={s.technician_id} type="button"
                    onClick={() => setForm({ ...form, technician_id: s.technician_id, time_window: s.suggested_window })}
                    data-testid={`suggestion-${s.technician_id}`}
                    className={`w-full rounded-lg border p-2.5 text-left text-xs transition-all ${form.technician_id === s.technician_id && form.time_window === s.suggested_window ? "border-[#028090] bg-white shadow" : "border-slate-200 bg-white/70 hover:border-[#2ec4b6]"} ${s.free_slots <= 0 ? "opacity-50" : ""}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-[#0f172a]">{s.technician_name} {s.van_id && `(${s.van_id})`}</span>
                      <span className={`rounded-full px-2 py-0.5 font-semibold ${s.free_slots > 0 ? "bg-emerald-100 text-[#1b4332]" : "bg-red-100 text-red-600"}`}>
                        {s.free_slots > 0 ? `${s.free_slots}/${s.capacity} frei` : "Tag voll"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-slate-500">Vorschlag: <span className="font-semibold text-[#028090]">{s.suggested_window}</span></p>
                    <p className="text-slate-400">{s.reason}</p>
                  </button>
                ))}
                {suggestions.suggestions.length === 0 && <p className="text-xs text-slate-400">Keine aktiven Techniker gefunden.</p>}
              </div>
            )}
            <Button className="mt-2 w-full bg-[#028090] hover:bg-[#026673]" onClick={propose} disabled={busy || !form.technician_id || !form.scheduled_date || !form.time_window} data-testid="propose-button">
              Terminvorschlag an Kunden senden
            </Button>
          </div>
          <div className="rounded-xl border border-slate-200 p-3" data-testid="part-section">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Ersatzteil-Suche & Reservierung (Halle)</p>
            {reservations.length > 0 && (
              <ul className="mb-2 space-y-1" data-testid="order-reservations">
                {reservations.map((r) => (
                  <li key={r.id} className="rounded-lg bg-[#e8f0ec] px-2.5 py-1.5 text-xs" data-testid={`reservation-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[#1b4332]">{r.sku} – {r.part_name} ({r.quantity}x)</span>
                      <span className="flex items-center gap-1">
                        {r.not_loaded && r.status === "reserved" && (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700" data-testid={`not-loaded-badge-${r.sku}`}>Nicht mitgenommen</span>
                        )}
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.status === "reserved" ? "bg-[#028090] text-white" : "bg-[#1b4332] text-white"}`}>
                          {r.status === "reserved" ? "Reserviert" : "Eingeladen"}
                        </span>
                      </span>
                    </div>
                    <p className="mt-0.5 text-slate-500">
                      Lagerplatz: <span className="font-mono2 font-semibold text-[#0f172a]">{r.bin || "–"}</span>
                      {" · "}Fahrzeug: {r.van_id}
                      {r.reserved_by && ` · von ${r.reserved_by}`}
                      {r.picked_by && ` · eingeladen von ${r.picked_by}`}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {!order.assigned_to && (
              <p className="mb-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700">Hinweis: Reservieren ist möglich, sobald ein Techniker zugewiesen und gespeichert wurde.</p>
            )}
            <PartSearch onReserve={order.assigned_to ? reserve : null} />
          </div>
          {order.payment && <InvoicePanel order={order} />}
          <div className="flex gap-2">
            <Button className="flex-1 bg-[#1b4332] hover:bg-[#143326]" onClick={save} disabled={busy} data-testid="order-save-button">
              {busy ? "Speichern…" : "Speichern"}
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => setShowInquiry(true)} data-testid="inquiry-open-button">
              <HelpCircle className="mr-1.5 h-4 w-4" /> Rückfrage an Kunden
            </Button>
          </div>
          {CANCELLABLE && (
            <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3" data-testid="cancel-order-section">
              {!showCancel ? (
                <Button variant="outline" size="sm" className="w-full border-red-300 text-red-600 hover:bg-red-100" onClick={() => setShowCancel(true)} data-testid="cancel-order-open">
                  <XCircle className="mr-1.5 h-4 w-4" /> Auftrag stornieren
                </Button>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs text-red-700">Grund (optional, z. B. „Kunde hat telefonisch storniert")</Label>
                  <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Kundenwunsch (telefonisch)" data-testid="cancel-order-reason" />
                  <div className="flex gap-2">
                    <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={cancelOrder} disabled={cancelBusy} data-testid="cancel-order-confirm">
                      {cancelBusy ? "Storniere…" : "Ja, endgültig stornieren"}
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={() => setShowCancel(false)} data-testid="cancel-order-abort">Abbrechen</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {showInquiry && <InquiryDialog order={order} onClose={() => setShowInquiry(false)} />}
      </DialogContent>
    </Dialog>
  );
};

const InvoicePanel = ({ order }) => {
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get(`/tech/jobs/${order.id}/quote`).then((r) => setQuote(r.data)).catch(() => {});
  }, [order.id]);

  const sendMail = async () => {
    setBusy(true);
    try {
      await API.post(`/office/orders/${order.id}/invoice/email`);
      toast.success("Rechnung per E-Mail an Kunden gesendet");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#1b4332]/25 bg-[#e8f0ec]/60 p-3" data-testid="invoice-panel">
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#1b4332]">
        Rechnung{order.invoice_number ? ` ${order.invoice_number}` : ""}
      </p>
      {quote && (
        <div className="space-y-1 text-xs" data-testid="invoice-breakdown">
          <div className="flex justify-between"><span>Anfahrts- &amp; Diagnosepauschale</span><span>{eur(quote.diagnosis)}</span></div>
          {quote.diagnosis_credited && <div className="flex justify-between text-[#2a9d8f]"><span>Verrechnung Pauschale (Reparatur erfolgt)</span><span>− {eur(quote.credit_amount ?? quote.diagnosis)}</span></div>}
          {quote.labor > 0 && <div className="flex justify-between"><span>Arbeitszeit ({quote.labor_minutes} Min. à {eur(quote.labor_rate)}/h)</span><span>{eur(quote.labor)}</span></div>}
          {quote.parts.map((p, i) => (
            <div key={i} className="flex justify-between"><span>{p.name} ({p.quantity}x)</span><span>{eur(p.vk * p.quantity)}</span></div>
          ))}
          {quote.diagnosis_already_paid > 0 && <div className="flex justify-between text-[#2a9d8f]"><span>Abzug: Bereits gezahlt</span><span>− {eur(quote.diagnosis_already_paid)}</span></div>}
          <div className="flex justify-between border-t border-[#1b4332]/20 pt-1 text-sm font-bold text-[#1b4332]"><span>Gesamt</span><span data-testid="invoice-total">{eur(quote.total)}</span></div>
          <div className="flex justify-between text-[11px] text-slate-400" data-testid="invoice-vat"><span>davon MwSt. 19 %</span><span>{eur(quote.vat)}</span></div>
        </div>
      )}
      {order.payment && (
        <p className="mt-2 text-xs text-slate-500">
          {order.payment.method_label} · {order.payment.paid
            ? <span className="font-bold text-[#1b4332]" data-testid="invoice-paid-badge">Bezahlt</span>
            : <span className="font-bold text-orange-600" data-testid="invoice-open-badge">Offen (Überweisung)</span>}
        </p>
      )}
      {order.invoice_url && (
        <div className="mt-2 flex gap-2">
          <a href={fileUrl(order.invoice_url)} target="_blank" rel="noreferrer"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#1b4332] px-3 py-1.5 text-xs font-bold text-white" data-testid="invoice-pdf-link">
            <Download className="h-3.5 w-3.5" /> PDF anzeigen
          </a>
          <Button size="sm" variant="outline" className="flex-1" onClick={sendMail} disabled={busy} data-testid="invoice-email-button">
            <Mail className="mr-1.5 h-3.5 w-3.5" /> {busy ? "Sende…" : "Per Mail an Kunden"}
          </Button>
        </div>
      )}
    </div>
  );
};

const Accounting = () => {
  const iso = (d) => {
    const x = new Date(d);
    x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
    return x.toISOString().slice(0, 10);
  };
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(1); return iso(d); });
  const [to, setTo] = useState(() => iso(new Date()));
  const [rows, setRows] = useState([]);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, rv] = await Promise.all([
        API.get("/office/invoices", { params: { from, to } }),
        API.get("/office/reports/revenue", { params: { from, to } }),
      ]);
      setRows(inv.data);
      setRep(rv.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const csvUrl = fileUrl(`/api/office/invoices/export.csv?from=${from}&to=${to}`);
  const zipUrl = fileUrl(`/api/office/invoices/export.zip?from=${from}&to=${to}`);

  return (
    <div className="space-y-4" data-testid="accounting-panel">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Von</Label>
            <Input type="date" className="mt-1" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="acct-from" />
          </div>
          <div>
            <Label>Bis</Label>
            <Input type="date" className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="acct-to" />
          </div>
          <div className="flex gap-2">
            <a href={csvUrl} data-testid="acct-csv-download"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#028090] px-3.5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#026673]">
              <FileSpreadsheet className="h-4 w-4" /> CSV (Buchungen)
            </a>
            <a href={zipUrl} data-testid="acct-zip-download"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#1b4332] px-3.5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#143326]">
              <FileArchive className="h-4 w-4" /> Alle PDFs (ZIP)
            </a>
          </div>
        </div>
      </div>

      {rep && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="acct-summary">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Umsatz (brutto)</p>
            <p className="mt-1 font-mono2 text-xl font-bold text-[#1b4332]" data-testid="acct-gross">{eur(rep.gross_total)}</p>
            <p className="text-[11px] text-slate-400">{rep.count} Rechnungen</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Bezahlt</p>
            <p className="mt-1 font-mono2 text-xl font-bold text-[#2a9d8f]" data-testid="acct-paid">{eur(rep.paid_total)}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Offen</p>
            <p className="mt-1 font-mono2 text-xl font-bold text-orange-600" data-testid="acct-open">{eur(rep.open_total)}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Netto / MwSt.</p>
            <p className="mt-1 font-mono2 text-xl font-bold text-[#0f172a]" data-testid="acct-net">{eur(rep.net_total)}</p>
            <p className="text-[11px] text-slate-400">darin MwSt. 19 %: {eur(rep.vat_total)}</p>
          </div>
        </div>
      )}

      {rep && rep.by_method.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="acct-by-method">
          <h3 className="font-heading mb-3 text-base font-semibold text-[#1b4332]">Umsatz nach Zahlungsart</h3>
          <div className="space-y-1.5">
            {rep.by_method.map((m) => (
              <div key={m.method} className="flex items-center justify-between text-sm" data-testid={`acct-method-${m.method}`}>
                <span className="text-slate-600">{m.label} <span className="text-slate-400">({m.count}×)</span></span>
                <span className="font-mono2 font-semibold">{eur(m.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="font-heading mb-3 text-base font-semibold text-[#1b4332]">Rechnungen im Zeitraum ({rows.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="acct-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="pb-2 pr-4">Rechnungsnr.</th><th className="pb-2 pr-4">Auftrag</th><th className="pb-2 pr-4">Datum</th>
                <th className="pb-2 pr-4">Kunde</th><th className="pb-2 pr-4">Zahlungsart</th><th className="pb-2 pr-4 text-right">Betrag</th><th className="pb-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.order_number} className="border-b border-slate-100" data-testid={`acct-row-${r.order_number}`}>
                  <td className="py-2.5 pr-4 font-mono2 font-bold text-[#1b4332]">{r.invoice_number || "–"}</td>
                  <td className="py-2.5 pr-4 font-mono2">{r.order_number}</td>
                  <td className="py-2.5 pr-4">{r.date}</td>
                  <td className="py-2.5 pr-4">{r.customer}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{r.method_label}</td>
                  <td className="py-2.5 pr-4 text-right font-mono2 font-semibold">{eur(r.amount)}</td>
                  <td className="py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${r.paid ? "bg-emerald-100 text-[#1b4332]" : "bg-orange-100 text-orange-700"}`}>
                      {r.paid ? "Bezahlt" : "Offen"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && !loading && <p className="py-8 text-center text-slate-400" data-testid="acct-empty">Keine Rechnungen im Zeitraum.</p>}
          {loading && <p className="py-8 text-center text-slate-400">Lade…</p>}
        </div>
      </div>
    </div>
  );
};

const INQUIRY_TEMPLATES = {
  kontakt: { label: "Kontaktdaten fehlen", text: "Guten Tag,\n\nfür die Disposition Ihres Reparaturauftrags fehlen uns leider noch vollständige Kontaktdaten (Telefonnummer / vollständige Adresse). Bitte antworten Sie kurz auf diese E-Mail.\n\nVielen Dank!\nIhr recova Serviceteam" },
  typenschild: { label: "Typenschild-Foto fehlt", text: "Guten Tag,\n\ndamit wir das passende Ersatzteil für Ihr Gerät vorab disponieren können, benötigen wir die E-Nummer / PNC vom Typenschild Ihres Geräts (Foto genügt). Bitte antworten Sie einfach auf diese E-Mail.\n\nVielen Dank!\nIhr recova Serviceteam" },
  termin: { label: "Terminabstimmung", text: "Guten Tag,\n\nzur Planung Ihres Reparaturtermins teilen Sie uns bitte Ihre bevorzugten Zeitfenster in der kommenden Woche mit. Antworten Sie einfach auf diese E-Mail.\n\nVielen Dank!\nIhr recova Serviceteam" },
};

const InquiryDialog = ({ order, onClose }) => {
  const [subject, setSubject] = useState(`Rückfrage zu Ihrem Auftrag ${order.order_number}`);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const applyTemplate = (key) => {
    setMessage(INQUIRY_TEMPLATES[key].text);
  };

  const send = async () => {
    if (!subject.trim() || !message.trim()) return toast.error("Betreff und Nachricht erforderlich");
    setBusy(true);
    try {
      await API.post(`/office/orders/${order.id}/inquiry`, { subject, message });
      toast.success(`Rückfrage an ${order.customer?.email} gesendet`);
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="inquiry-dialog">
        <DialogHeader><DialogTitle>Rückfrage an {order.customer?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">An: {order.customer?.email || "keine E-Mail hinterlegt"}</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(INQUIRY_TEMPLATES).map(([k, t]) => (
            <button key={k} type="button" onClick={() => applyTemplate(k)} data-testid={`template-${k}`}
              className="rounded-full border border-[#028090]/40 bg-cyan-50 px-3 py-1 text-xs font-semibold text-[#028090] transition-colors hover:bg-cyan-100">
              {t.label}
            </button>
          ))}
        </div>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Betreff" data-testid="inquiry-subject" />
        <Textarea rows={7} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ihre Nachricht an den Kunden…" data-testid="inquiry-message" />
        <Button className="bg-[#1b4332] hover:bg-[#143326]" onClick={send} disabled={busy || !order.customer?.email} data-testid="inquiry-send">
          {busy ? "Sende…" : "E-Mail senden"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const PLAN_STATUSES = ["terminvorschlag", "termin_vereinbart", "techniker_unterwegs", "vor_ort", "teil_reserviert_halle", "wartet_auf_lieferant", "bereit_fuer_zweittermin"];
const DAY_NAMES = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const Planner = ({ onOpenOrder }) => {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d;
  });
  const [orders, setOrders] = useState([]);
  const [techs, setTechs] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [tours, setTours] = useState([]);

  useEffect(() => {
    API.get("/office/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(fmtErr(e)));
    API.get("/office/technicians").then((r) => setTechs(r.data)).catch(() => {});
    API.get("/office/reservations").then((r) => setReservations(r.data)).catch(() => {});
    API.get("/office/tours").then((r) => setTours(r.data)).catch(() => {});
  }, []);

  const resByOrder = {};
  reservations.forEach((r) => {
    (resByOrder[r.order_id] = resByOrder[r.order_id] || []).push(r);
  });

  const iso = (d) => {
    const x = new Date(d);
    x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
    return x.toISOString().slice(0, 10);
  };
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const shift = (n) => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() + n * 7); return d; });
  const jobsFor = (tid, day) =>
    orders.filter((o) => o.scheduled_date === iso(day) && PLAN_STATUSES.includes(o.status) && (tid ? o.assigned_to === tid : !o.assigned_to));
  const todayISO = iso(new Date());

  const renderCell = (tid, day) => {
    const jobs = jobsFor(tid, day);
    return (
      <td key={iso(day)} className={`min-w-[130px] border border-slate-100 p-1.5 align-top ${iso(day) === todayISO ? "bg-[#e8f0ec]/50" : "bg-white"}`}>
        {jobs.map((j) => (
          <button key={j.id} onClick={() => onOpenOrder(j)} data-testid={`planner-job-${j.order_number}`}
            className={`mb-1 block w-full rounded-lg border px-2 py-1.5 text-left text-xs transition-all hover:shadow ${STATUS_COLORS[j.status]}`}>
            <span className="font-mono2 font-bold">{j.order_number}</span>
            <span className="block">{j.customer?.zip} {j.customer?.city}</span>
            <span className="block text-[10px] opacity-80">{j.time_window || "Zeit offen"} · {j.appliance?.type_label}</span>
            {(resByOrder[j.id] || []).map((r) => (
              <span key={r.id} className="mt-0.5 block rounded bg-white/80 px-1 py-0.5 text-[10px] font-semibold text-[#1b4332]" data-testid={`planner-reservation-${j.order_number}`}>
                {r.status === "reserved" ? (r.not_loaded ? "⚠ Nicht mitgenommen" : "Reserviert") : "Eingeladen"}: {r.sku} ×{r.quantity} · {r.status === "reserved" ? (r.bin || "Halle") : r.van_id}
              </span>
            ))}
          </button>
        ))}
        {jobs.length === 0 && <span className="text-xs text-slate-300">–</span>}
      </td>
    );
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="planner-panel">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-[#1b4332]">Dispo-Planer – Wochenübersicht</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="planner-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-semibold text-slate-600" data-testid="planner-week-label">
            KW ab {days[0].toLocaleDateString("de-DE")}
          </span>
          <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="planner-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      {tours.length > 0 && (
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="tour-status-strip">
          {tours.map((t) => {
            const tour = t.tour;
            const state = !tour ? "noch nicht gestartet" : tour.ended_at ? "Tour beendet" : tour.started_at ? "unterwegs" : "Pickliste läuft";
            const stateCls = !tour ? "bg-slate-100 text-slate-500" : tour.ended_at ? "bg-[#1b4332] text-white" : tour.started_at ? "bg-[#2ec4b6] text-[#1b4332]" : "bg-amber-100 text-amber-700";
            return (
              <div key={t.technician_id} className="rounded-xl border border-slate-200 p-3" data-testid={`tour-status-${t.technician_name.replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-[#0f172a]">{t.technician_name} <span className="font-normal text-slate-400">({t.van_id})</span></p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${stateCls}`} data-testid={`tour-state-${t.technician_id}`}>{state}</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  Pickliste: {t.picklist_total - t.picklist_pending}/{t.picklist_total} entschieden
                  {tour?.force_started && <span className="ml-1 font-semibold text-amber-600">(mit offenen Positionen gestartet)</span>}
                </p>
                {t.not_loaded.map((n) => (
                  <p key={n.sku} className="mt-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`tour-not-loaded-${n.sku}`}>
                    Nicht mitgenommen: {n.sku} – {n.part_name} ({n.bin || "Halle"}) · {n.order_number}
                  </p>
                ))}
              </div>
            );
          })}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" data-testid="planner-grid">
          <thead>
            <tr>
              <th className="border border-slate-100 bg-slate-50 p-2 text-left text-xs uppercase tracking-wider text-slate-400">Techniker</th>
              {days.map((d, i) => (
                <th key={i} className={`border border-slate-100 p-2 text-center text-xs ${iso(d) === todayISO ? "bg-[#1b4332] text-white" : "bg-slate-50 text-slate-500"}`}>
                  {DAY_NAMES[i]}<br /><span className="font-normal">{d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {techs.map((t) => (
              <tr key={t.id} data-testid={`planner-row-${t.id}`}>
                <td className="border border-slate-100 bg-slate-50 p-2 text-xs font-bold text-[#0f172a]">
                  {t.name}<span className="block font-normal text-slate-400">{t.van_id} · {t.van_plate}</span>
                </td>
                {days.map((d) => renderCell(t.id, d))}
              </tr>
            ))}
            <tr data-testid="planner-row-unassigned">
              <td className="border border-slate-100 bg-amber-50 p-2 text-xs font-bold text-amber-700">Nicht zugewiesen</td>
              {days.map((d) => renderCell(null, d))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-400">Klick auf einen Auftrag öffnet die Detailansicht zum Disponieren.</p>
    </div>
  );
};

const Customers = ({ onOpenOrder }) => {
  const [customers, setCustomers] = useState([]);
  const [sel, setSel] = useState(null);
  const [selOrders, setSelOrders] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    API.get("/office/customers").then((r) => setCustomers(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const open = async (c) => {
    setSel(c);
    const r = await API.get("/office/orders");
    setSelOrders(r.data.filter((o) => (o.customer?.email || `phone:${o.customer?.phone}`) === c.key));
  };

  const filtered = customers.filter((c) =>
    !search || [c.name, c.email, c.phone, c.zip, c.city].filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="customers-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading text-lg font-semibold text-[#1b4332]">Kundenübersicht</h3>
        <Input className="w-64" placeholder="Suche: Name, PLZ, Telefon…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="customer-search" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="pb-2 pr-4">Kunde</th><th className="pb-2 pr-4">Kontakt</th><th className="pb-2 pr-4">PLZ / Ort</th><th className="pb-2 pr-4">Aufträge</th><th className="pb-2 pr-4">Umsatz</th><th className="pb-2">Zuletzt</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.key} className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-[#e8f0ec]/50" onClick={() => open(c)} data-testid={`customer-row-${c.key}`}>
                <td className="py-2.5 pr-4">
                  <span className="font-semibold text-[#0f172a]">{c.name}</span>
                  {c.returning && <span className="ml-2 rounded-full bg-[#1b4332] px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`returning-badge-${c.key}`}>Wiederkehrend</span>}
                </td>
                <td className="py-2.5 pr-4 text-slate-500">{c.email || c.phone}</td>
                <td className="py-2.5 pr-4">{c.zip} {c.city}</td>
                <td className="py-2.5 pr-4"><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold">{c.orders_count}</span></td>
                <td className="py-2.5 pr-4">{eur(c.revenue)}</td>
                <td className="py-2.5 text-slate-500">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString("de-DE") : "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="py-8 text-center text-slate-400">Keine Kunden gefunden.</p>}
      </div>

      {sel && (
        <Dialog open onOpenChange={() => setSel(null)}>
          <DialogContent className="max-w-lg" data-testid="customer-detail-dialog">
            <DialogHeader><DialogTitle>{sel.name}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500">{sel.email || "–"} · {sel.phone || "–"} · {sel.zip} {sel.city}</p>
            <p className="text-xs text-slate-400">{sel.orders_count} Aufträge · Umsatz {eur(sel.revenue)}</p>
            <div className="max-h-72 space-y-2 overflow-y-auto" data-testid="customer-orders">
              {selOrders.map((o) => (
                <button key={o.id} onClick={() => { setSel(null); onOpenOrder(o); }} data-testid={`customer-order-${o.order_number}`}
                  className="flex w-full items-center justify-between rounded-lg border border-slate-200 p-3 text-left text-sm transition-all hover:border-[#2ec4b6] hover:shadow">
                  <div>
                    <span className="font-mono2 font-bold">{o.order_number}</span>
                    <span className="ml-2 text-slate-500">{o.appliance?.brand} {o.appliance?.type_label}</span>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</span>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const PhoneIntakeForm = ({ onCreated }) => {
  const empty = { customer: { name: "", email: "", phone: "", street: "", zip: "", city: "" }, appliance: { type: "waschmaschine", brand: "", model: "", e_number: "", serial_number: "" }, fault: { error_code: "", issues: [], description: "" }, time_window: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const setC = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));
  const setA = (k, v) => setForm((f) => ({ ...f, appliance: { ...f.appliance, [k]: v } }));

  const submit = async () => {
    if (!form.customer.name || !form.customer.phone || !form.customer.zip) return toast.error("Name, Telefon und PLZ sind Pflicht");
    setBusy(true);
    try {
      const r = await API.post("/office/intake", form);
      toast.success(`Auftrag ${r.data.order_number} angelegt`);
      setForm(empty);
      onCreated();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="phone-intake-form">
      <h3 className="font-heading text-lg font-semibold text-[#1b4332]">Telefon-Aufnahme (Schnellerfassung)</h3>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input placeholder="Name *" value={form.customer.name} onChange={(e) => setC("name", e.target.value)} data-testid="intake-name" />
        <Input placeholder="Telefon *" value={form.customer.phone} onChange={(e) => setC("phone", e.target.value)} data-testid="intake-phone" />
        <Input placeholder="E-Mail" value={form.customer.email} onChange={(e) => setC("email", e.target.value)} data-testid="intake-email" />
        <Input placeholder="Straße & Nr." value={form.customer.street} onChange={(e) => setC("street", e.target.value)} data-testid="intake-street" />
        <Input placeholder="PLZ *" value={form.customer.zip} onChange={(e) => setC("zip", e.target.value)} data-testid="intake-zip" />
        <Input placeholder="Ort" value={form.customer.city} onChange={(e) => setC("city", e.target.value)} data-testid="intake-city" />
        <select className="rounded-md border border-slate-300 px-2 py-2 text-sm" value={form.appliance.type} onChange={(e) => setA("type", e.target.value)} data-testid="intake-appliance-type">
          <option value="waschmaschine">Waschmaschine</option>
          <option value="spuelmaschine">Spülmaschine</option>
          <option value="trockner">Trockner</option>
          <option value="backofen">Backofen / Herd</option>
          <option value="kuehlschrank">Kühlschrank</option>
        </select>
        <Input placeholder="Marke" value={form.appliance.brand} onChange={(e) => setA("brand", e.target.value)} data-testid="intake-brand" />
        <Input placeholder="Modell" value={form.appliance.model} onChange={(e) => setA("model", e.target.value)} data-testid="intake-model" />
        <Input placeholder="E-Nummer / PNC" value={form.appliance.e_number} onChange={(e) => setA("e_number", e.target.value)} data-testid="intake-enumber" />
        <Input placeholder="Seriennummer" value={form.appliance.serial_number} onChange={(e) => setA("serial_number", e.target.value)} data-testid="intake-serial" />
        <Input placeholder="Fehlercode" value={form.fault.error_code} onChange={(e) => setForm((f) => ({ ...f, fault: { ...f.fault, error_code: e.target.value } }))} data-testid="intake-error-code" />
      </div>
      <Textarea className="mt-3" rows={2} placeholder="Fehlerbeschreibung laut Anrufer…" value={form.fault.description}
        onChange={(e) => setForm((f) => ({ ...f, fault: { ...f.fault, description: e.target.value } }))} data-testid="intake-description" />
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="intake-part-search">
        <Label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Ersatzteil-Verfügbarkeit prüfen (optional)</Label>
        <PartSearch />
      </div>
      <Button className="mt-4 bg-[#1b4332] hover:bg-[#143326]" onClick={submit} disabled={busy} data-testid="intake-submit">
        {busy ? "Anlegen…" : "Auftrag anlegen"}
      </Button>
    </div>
  );
};

export default function OfficeDashboard() {
  const [tab, setTab] = useState("auftraege");
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [technicians, setTechnicians] = useState([]);
  const [selected, setSelected] = useState(null);
  const [queue, setQueue] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [convertDraft, setConvertDraft] = useState(null);
  const [convertForm, setConvertForm] = useState(null);
  const [showDoneOrders, setShowDoneOrders] = useState(false);

  const DONE_STATUSES = ["abgeschlossen", "totalschaden", "storniert"];
  const openOrders = statusFilter ? orders : orders.filter((o) => !DONE_STATUSES.includes(o.status));
  const doneOrders = orders.filter((o) => DONE_STATUSES.includes(o.status));

  const loadOrders = useCallback(() => {
    API.get("/office/orders", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => setOrders(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [statusFilter]);
  const loadQueue = useCallback(() => API.get("/office/intake-queue").then((r) => setQueue(r.data)).catch(() => {}), []);
  const loadClusters = useCallback(() => API.get("/dispatch/clusters").then((r) => setClusters(r.data)).catch(() => {}), []);

  useEffect(() => {
    API.get("/office/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => { if (tab === "email") loadQueue(); if (tab === "dispo") loadClusters(); }, [tab, loadQueue, loadClusters]);

  const [imapBusy, setImapBusy] = useState(false);
  const fetchImap = async () => {
    setImapBusy(true);
    try {
      const r = await API.post("/office/intake-queue/fetch-imap");
      toast.success(r.data.fetched > 0 ? `${r.data.fetched} neue E-Mail(s) abgerufen` : "Keine neuen E-Mails im Postfach");
      loadQueue();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setImapBusy(false);
    }
  };

  const openConvert = (draft) => {
    setConvertDraft(draft);
    setConvertForm({
      customer: { name: draft.parsed?.name || "", email: draft.from_email, phone: "", street: "", zip: draft.parsed?.zip || "", city: "" },
      appliance: { type: draft.parsed?.appliance_type || "waschmaschine", brand: "", model: "", e_number: "" },
      fault: { error_code: "", issues: [], description: draft.parsed?.description || draft.body },
    });
  };

  const doConvert = async () => {
    try {
      const r = await API.post(`/office/intake-queue/${convertDraft.id}/convert`, convertForm);
      toast.success(`Auftrag ${r.data.order_number} angelegt`);
      setConvertDraft(null);
      loadQueue();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <DashboardLayout title="Disposition & Auftragsverwaltung" subtitle="Backoffice"
      sections={BACKOFFICE_SECTIONS.map((s) => ({ ...s, active: s.to === "/app/office" }))}
      active={tab} onNav={setTab}
      nav={[
        { key: "auftraege", label: "Aufträge", icon: <ClipboardList className="h-4 w-4" /> },
        { key: "planer", label: "Dispo-Planer", icon: <CalendarDays className="h-4 w-4" /> },
        { key: "kunden", label: "Kunden", icon: <Users className="h-4 w-4" /> },
        { key: "telefon", label: "Telefon-Intake", icon: <PhoneCall className="h-4 w-4" /> },
        { key: "email", label: "E-Mail-Queue", icon: <Mail className="h-4 w-4" />, badge: queue.length },
        { key: "dispo", label: "PLZ-Disposition", icon: <Map className="h-4 w-4" /> },
        { key: "karte", label: "Karte", icon: <MapPin className="h-4 w-4" /> },
        { key: "buchhaltung", label: "Buchhaltung", icon: <Calculator className="h-4 w-4" /> },
      ]}>
      {tab === "planer" && <Planner onOpenOrder={(o) => setSelected(o)} />}
      {tab === "karte" && <RouteMap />}
      {tab === "buchhaltung" && <Accounting />}
      {tab === "kunden" && <Customers onOpenOrder={(o) => setSelected(o)} />}
      {tab === "auftraege" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="orders-panel">
          <div className="mb-4 flex items-center gap-3">
            <Label>Status-Filter:</Label>
            <select className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="orders-status-filter">
              <option value="">Alle</option>
              {STATUS_KEYS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="pb-2 pr-4">Auftrag</th><th className="pb-2 pr-4">Kunde</th><th className="pb-2 pr-4">Gerät</th><th className="pb-2 pr-4">PLZ</th><th className="pb-2 pr-4">Termin</th><th className="pb-2 pr-4">Techniker</th><th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {openOrders.map((o) => (
                  <tr key={o.id} className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-[#e8f0ec]/50"
                    onClick={() => setSelected(o)} data-testid={`order-row-${o.order_number}`}>
                    <td className="py-2.5 pr-4 font-mono2 font-semibold">{o.order_number}</td>
                    <td className="py-2.5 pr-4">{o.customer?.name}</td>
                    <td className="py-2.5 pr-4">{o.appliance?.brand} {o.appliance?.type_label}</td>
                    <td className="py-2.5 pr-4">{o.customer?.zip}</td>
                    <td className="py-2.5 pr-4">{o.scheduled_date || "–"}</td>
                    <td className="py-2.5 pr-4">{o.assigned_to_name || "–"}</td>
                    <td className="py-2.5"><span className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {openOrders.length === 0 && <p className="py-8 text-center text-slate-400">Keine offenen Aufträge für diesen Filter.</p>}
          </div>
          {!statusFilter && doneOrders.length > 0 && (
            <div className="mt-4">
              <button onClick={() => setShowDoneOrders(!showDoneOrders)} className="text-sm font-semibold text-[#028090] hover:underline" data-testid="toggle-done-orders">
                {showDoneOrders ? "▾" : "▸"} Abgeschlossene Aufträge ({doneOrders.length}) {showDoneOrders ? "ausblenden" : "einblenden"}
              </button>
              {showDoneOrders && (
                <div className="mt-2 space-y-1.5" data-testid="done-orders-list">
                  {doneOrders.map((o) => (
                    <button key={o.id} onClick={() => setSelected(o)} data-testid={`done-order-${o.order_number}`}
                      className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm opacity-75 transition-opacity hover:opacity-100">
                      <span><span className="font-mono2 font-bold">{o.order_number}</span> · {o.customer?.name} · {o.appliance?.brand} {o.appliance?.type_label}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "telefon" && <PhoneIntakeForm onCreated={loadOrders} />}

      {tab === "email" && (
        <div className="space-y-4" data-testid="email-queue-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Eingehende E-Mails werden automatisch als Auftrags-Entwürfe erkannt.</p>
            <Button className="bg-[#028090] hover:bg-[#026673]" onClick={fetchImap} disabled={imapBusy} data-testid="fetch-imap-button">
              <Mail className="mr-2 h-4 w-4" /> {imapBusy ? "Rufe ab…" : "IMAP-Postfach abrufen"}
            </Button>
          </div>
          {queue.map((d) => (
            <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`draft-${d.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-[#0f172a]">{d.subject}</p>
                  <p className="text-xs text-slate-400">Von: {d.from_email}</p>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{d.body}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" className="bg-[#1b4332] hover:bg-[#143326]" onClick={() => openConvert(d)} data-testid={`convert-draft-${d.id}`}>In Auftrag umwandeln</Button>
                  <Button size="sm" variant="outline" onClick={async () => { await API.delete(`/office/intake-queue/${d.id}`); loadQueue(); }} data-testid={`discard-draft-${d.id}`}><X className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
          {queue.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400" data-testid="queue-empty">Keine offenen E-Mails in der Queue.</p>}
        </div>
      )}

      {tab === "dispo" && (
        <div className="space-y-4" data-testid="dispatch-panel">
          <p className="text-sm text-slate-500">PLZ-Clustering gruppiert offene Einsätze geografisch für effiziente Routen.</p>
          {clusters.map((c) => (
            <div key={c.cluster} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`cluster-${c.cluster}`}>
              <div className="mb-3 flex items-center justify-between">
                <p className="font-heading text-lg font-bold text-[#1b4332]">PLZ {c.cluster} <span className="ml-2 rounded-full bg-[#e8f0ec] px-2 py-0.5 text-xs font-semibold">{c.count} Einsätze</span></p>
              </div>
              <ul className="space-y-1.5 text-sm">
                {c.orders.map((o) => (
                  <li key={o.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
                    <span className="font-mono2 font-semibold">{o.order_number}</span>
                    <span>{o.customer?.zip} {o.customer?.city}</span>
                    <span className="text-slate-400">{o.scheduled_date || "kein Termin"}</span>
                    <span className={`ml-auto rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {clusters.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400">Aktuell keine disponiblen Einsätze.</p>}
        </div>
      )}

      {selected && <OrderDetail order={selected} technicians={technicians} onClose={() => setSelected(null)} onSaved={loadOrders} />}

      {convertDraft && convertForm && (
        <Dialog open onOpenChange={() => setConvertDraft(null)}>
          <DialogContent className="max-w-lg" data-testid="convert-dialog">
            <DialogHeader><DialogTitle>E-Mail in Auftrag umwandeln</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Input placeholder="Name *" value={convertForm.customer.name} onChange={(e) => setConvertForm((f) => ({ ...f, customer: { ...f.customer, name: e.target.value } }))} data-testid="convert-name" />
              <Input placeholder="Telefon" value={convertForm.customer.phone} onChange={(e) => setConvertForm((f) => ({ ...f, customer: { ...f.customer, phone: e.target.value } }))} data-testid="convert-phone" />
              <Input placeholder="E-Mail" value={convertForm.customer.email} onChange={(e) => setConvertForm((f) => ({ ...f, customer: { ...f.customer, email: e.target.value } }))} data-testid="convert-email" />
              <Input placeholder="PLZ *" value={convertForm.customer.zip} onChange={(e) => setConvertForm((f) => ({ ...f, customer: { ...f.customer, zip: e.target.value } }))} data-testid="convert-zip" />
              <Input placeholder="Straße" value={convertForm.customer.street} onChange={(e) => setConvertForm((f) => ({ ...f, customer: { ...f.customer, street: e.target.value } }))} data-testid="convert-street" />
              <Input placeholder="Ort" value={convertForm.customer.city} onChange={(e) => setConvertForm((f) => ({ ...f, customer: { ...f.customer, city: e.target.value } }))} data-testid="convert-city" />
            </div>
            <Textarea rows={2} value={convertForm.fault.description} onChange={(e) => setConvertForm((f) => ({ ...f, fault: { ...f.fault, description: e.target.value } }))} data-testid="convert-description" />
            <Button className="bg-[#1b4332] hover:bg-[#143326]" onClick={doConvert} data-testid="convert-submit">Auftrag anlegen</Button>
          </DialogContent>
        </Dialog>
      )}
    </DashboardLayout>
  );
}
